import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Clock, Users, Layers, Building2,
  BarChart3, Database, PieChart, Brain,
  Calculator, TrendingUp, GraduationCap,
  Video, Building, Laptop,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Clock, value: "100,000+", label: "Learning Hours Delivered", desc: "Accelerating enterprise data and AI adoption through immersive, hands-on learning experiences." },
  { icon: Users, value: "500+", label: "Enterprise Batches Delivered", desc: "Successfully trained cross-functional teams in Data Analytics, Machine Learning, and AI technologies." },
  { icon: Layers, value: "30+", label: "Emerging Technology Domains", desc: "Comprehensive learning across Data Analytics, AI, Machine Learning, Deep Learning, Cloud, and more." },
  { icon: Building2, value: "50+", label: "Fortune & High-Growth Companies Served", desc: "Trusted by leading enterprises to build scalable data and AI capabilities." }
];

const LEARNING_OUTCOMES = [
  { icon: BarChart3, title: "Data Analytics", desc: "Develop practical data analytics skills to collect, clean, analyze, and interpret business data for informed decision-making using modern analytical techniques." },
  { icon: Database, title: "Data Science", desc: "Explore the complete data science lifecycle, from data preparation and statistical analysis to predictive modeling and business insights using industry-standard tools." },
  { icon: Layers, title: "Big Data Analytics", desc: "Analyze large-scale structured and unstructured datasets using big data technologies to uncover patterns, improve operational efficiency, and support enterprise decision-making." },
  { icon: PieChart, title: "Data Visualization", desc: "Create interactive dashboards and compelling visual reports using leading BI and visualization tools to communicate insights with clarity and impact." },
  { icon: Brain, title: "Machine Learning & AI", desc: "Learn supervised and unsupervised machine learning techniques, predictive analytics, feature engineering, and model evaluation for enterprise AI applications." },
  { icon: Calculator, title: "Statistics for AI & ML", desc: "Master statistical concepts including probability, hypothesis testing, regression, distributions, and data interpretation that form the foundation of AI and machine learning." },
  { icon: TrendingUp, title: "Data Interpretation & Business Insights", desc: "Convert raw data into meaningful business insights through exploratory analysis, KPI measurement, trend identification, and evidence-based decision-making." },
  { icon: GraduationCap, title: "Capstone Projects", desc: "Apply newly acquired knowledge through enterprise-focused capstone projects, working with real-world datasets to solve practical analytics and AI challenges." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive instructor-led online sessions featuring hands-on labs, practical exercises, and real-world enterprise case studies." },
  { icon: Building, title: "Classroom Bootcamps", desc: "Immersive instructor-led workshops delivered onsite or at Skillsgain training centers." },
  { icon: Laptop, title: "Self-Paced Learning", desc: "On-demand access to learning resources, recorded sessions, assessments, and practice exercises." },
  { icon: Layers, title: "Blended Learning", desc: "Combine virtual instruction, classroom workshops, self-paced modules, and enterprise capstone projects for maximum knowledge retention." }
];

const BY_THE_NUMBERS = [
  { value: "250+", label: "Enterprise Clients" },
  { value: "200+", label: "Corporate Learning Programs" },
  { value: "500+", label: "Instructor-Led Workshops" },
  { value: "100+", label: "Enterprise Trainers" }
];

const DataAnalyticsAIPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE DATA, ANALYTICS & AI SOLUTIONS"
        title="Enterprise Data, Analytics & AI Training for Future-Ready Teams"
        subtitle="Empower your workforce with industry-aligned Data Analytics Courses that build practical expertise in Data Visualization, Machine Learning, Deep Learning, Data Interpretation, and AI-driven decision-making. Skillsgain delivers customized enterprise learning programs designed to help organizations upskill teams, accelerate digital transformation, and drive measurable business outcomes."
      />

      {/* Measurable Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">MEASURABLE LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Delivering measurable business outcomes through enterprise Data & AI training</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Enterprise Learning Paths */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">ENTERPRISE LEARNING PATHS</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Build practical Data Analytics & AI capabilities that transform business performance</h2>
            <p className="mt-3 text-gray-600">Our instructor-led learning programs combine real-world projects, business use cases, and hands-on labs to help professionals confidently apply modern data analytics and artificial intelligence technologies.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Flexible Enterprise Learning */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">FLEXIBLE ENTERPRISE LEARNING</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Choose the learning experience that fits your workforce</h2>
              <p className="mt-3 text-gray-600">Whether you're training a single department or a global workforce, Skillsgain provides scalable learning experiences tailored to your business objectives.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + testimonial */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#F57C20] mb-4"/>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain helped our analytics teams strengthen their data analytics capabilities through customized learning programs. The hands-on projects, real-world case studies, and practical learning approach significantly improved our team's confidence in applying data-driven decision-making across business functions.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR ENTERPRISE TEAMS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Transform Your Workforce with Data, Analytics & AI Skills</h2>
                <p className="mt-3 text-gray-300">Build high-performing teams with enterprise training in Data Analytics, Machine Learning, Deep Learning, Data Visualization, Data Interpretation, and AI technologies. Skillsgain delivers customized learning experiences that help organizations accelerate innovation and improve business performance.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default DataAnalyticsAIPage;
